import { PaymentMethod } from "services/paymentMethods";
import { RadioGroup } from "@mui/material";
import { PlanRadio, RadioCheckedIcon, RadioIcon } from "../RadioStyles";
import { type ChangeEvent, type MouseEvent, ReactNode, useState } from "react";
import classnames from "classnames";
import { PricingModelItem } from "../../../services/pricingModel/model";
import CheckoutSummary from "../CheckoutSummary";
import PaymentResultModal from "../PaymentResultModal";
import SubmitButton from "../SubmitButton";
import PaymentMethodIcons from "components/PaymentMethodIcons";
import styles from "./style.module.scss";
import { PurchaseResult } from "services/subscriptions";

import { logError } from "services/logging";
type Props = {
  onSavedMethodPurchase: () => Promise<PurchaseResult>;
  paymentMethods: PaymentMethod[];
  pricingModelItem: PricingModelItem;
  onSuccess: () => void;
  purchasing: boolean;
  children?: ReactNode;
};

export default function ExistingPaymentMethods({
  onSavedMethodPurchase,
  paymentMethods,
  pricingModelItem,
  onSuccess,
  purchasing,
  children,
}: Props) {
  const [selected, setSelected] = useState<PaymentMethod | undefined>(paymentMethods[0]);
  const [showModalDialog, setShowModalDialog] = useState<PurchaseResult | null>(null);

  // The caller only picks this mode when a saved method exists, but the prop is a
  // plain array, so nothing in the type carries that.
  if (!selected) {
    return null;
  }

  const hdOnChange = (evt: ChangeEvent<HTMLInputElement>) => {
    evt.preventDefault();

    const id = evt.target.value;
    const newSelection = paymentMethods.find(pm => pm.id === id);
    if (newSelection) {
      setSelected(newSelection);
    }
  };

  const hdOnSubmit = async (evt: MouseEvent<HTMLButtonElement>) => {
    evt.preventDefault();
    if (purchasing) {
      return;
    }
    const result = await onSavedMethodPurchase();
    if (result !== "failed_reload_on_close") {
      setShowModalDialog(result);
    }
  };

  return (
    <div>
      {showModalDialog === "success" && (
        <PaymentResultModal
          variant="success"
          plan={pricingModelItem}
          onClose={onSuccess}
        />
      )}
      {showModalDialog === "failed" && (
        <PaymentResultModal variant="failed" onClose={() => setShowModalDialog(null)} />
      )}
      <RadioGroup defaultValue={selected.id} value={selected.id} onChange={hdOnChange}>
        {paymentMethods.map(pm => (
          <PaymentMethodRow
            key={pm.id}
            paymentMethod={pm}
            isSelected={pm.id === selected.id}
          />
        ))}
      </RadioGroup>
      <CheckoutSummary pricingItem={pricingModelItem}>
        <SubmitButton
          hdSubmit={evt => {
            hdOnSubmit(evt).catch(logError);
          }}
          processing={purchasing}
          type={pricingModelItem.purchase_student_type}
        />
        {children}
      </CheckoutSummary>
    </div>
  );
}

type PaymentMethodRowProps = {
  paymentMethod: PaymentMethod;
  isSelected: boolean;
};

function PaymentMethodRow({ paymentMethod, isSelected }: PaymentMethodRowProps) {
  return (
    <div className={classnames(styles.methodRow, { active: isSelected })}>
      <div className={styles.methodLabel}>
        <PaymentMethodIcons
          className={styles.paymentMethodIcon}
          paymentMethod={paymentMethod}
        />
        <span>•••• •••• •••• {paymentMethod.card.last4}</span>
      </div>
      <PlanRadio
        checked={isSelected}
        disableRipple
        color="default"
        checkedIcon={<RadioCheckedIcon />}
        icon={<RadioIcon />}
      />
    </div>
  );
}
